import { getIsNativePlatform } from 'utils/client/mobile';
import { OptionProfitCalculator as OPCB } from 'constants/protobufs/opc';
import { API_URL } from 'constants/api';

export interface OpcData {
  chains: any; // Map of ticker chains
  stocks: any[]; // Stock info array
  rates: any; // Risk-free rates map
}

export async function fetchOpcData(ticker: string): Promise<OpcData> {
  const isMobile = getIsNativePlatform() || process.env.TARGET_PLATFORM === 'MOBILE';

  // TEMP: `?source=eod` forces the client to hit /v1/opc/eod which serves
  // options_eod data only. Lets us verify the EOD fallback shape end-to-end
  // in the calculator UI. Remove this block + the /v1/opc/eod endpoint once
  // the fallback is verified in prod.
  const useEod =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('source') === 'eod';
  const endpoint = useEod ? '/v1/opc/eod' : '/v1/opc';

  try {
    console.log('[OPC] Fetching data for ticker:', ticker);
    console.log('[OPC] Platform:', isMobile ? 'Mobile' : 'Web');
    console.log('[OPC] Format:', isMobile ? 'JSON' : 'Binary protobuf');
    if (useEod) console.log('[OPC] SOURCE=EOD (test mode)');

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify({
        ticker,
        format: isMobile ? 'json' : undefined, // Request JSON for mobile
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Mobile: JSON response, use directly
    if (isMobile) {
      const data = await response.json();
      console.log('[OPC] Received JSON data');
      return data;
    }

    // Web: Binary protobuf, deserialize
    const arrayBuffer = await response.arrayBuffer();
    console.log('[OPC] Received binary data, size:', arrayBuffer.byteLength);

    const uint8Array = new Uint8Array(arrayBuffer);
    const deserializedData = OPCB.deserializeBinary(uint8Array);
    const jsonData = deserializedData.toObject();

    // if (process.env.APP_STAGE === 'development') {
    //   console.log('jsonData', jsonData);
    // }

    console.log('[OPC] Successfully deserialized protobuf');

    return {
      chains: jsonData.c?.tickerChains,
      stocks: jsonData.s ?? [],
      rates: jsonData.r?.rates,
    };
  } catch (error) {
    console.error('[OPC] Failed to fetch OPC data:', error);
    console.error('[OPC] Error details:', {
      name: error instanceof Error ? error.constructor.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      platform: isMobile ? 'Mobile' : 'Web',
    });
    throw error;
  }
}
